import React from 'react';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SVG_HEIGHT = 320;
const MARGIN = { top: 28, right: 28, bottom: 62, left: 54 };

const parseMonth = (value) => {
  const [monthLabel, yearLabel] = String(value || '').trim().split(/\s+/);
  const monthIndex = MONTH_LABELS.indexOf(monthLabel);
  const year = Number(yearLabel);
  return monthIndex < 0 || !Number.isInteger(year) ? null : new Date(year, monthIndex, 1);
};

const ActiveStudentsAllTimeLineChart = ({ data = [] }) => {
  const [tooltip, setTooltip] = React.useState({show: false, x: 0, y: 0, label: '', value: 0});
  const points = React.useMemo(() => (
    (Array.isArray(data) ? data : [])
      .map((item) => ({
        date: parseMonth(item?.month),
        label: String(item?.month || ''),
        value: Number(item?.activeStudents),
      }))
      .filter((item) => item.date && Number.isFinite(item.value))
      .sort((left, right) => left.date.getTime() - right.date.getTime())
  ), [data]);

  if (!points.length || !points.some((point) => point.value > 0)) {
    return <div className='text-sm text-slate-500'>No submitted timesheet student data is available for this school yet.</div>;
  }

  const chartWidth = Math.max(860, MARGIN.left + MARGIN.right + (points.length - 1) * 64);
  const innerWidth = chartWidth - MARGIN.left - MARGIN.right;
  const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const xStep = points.length > 1 ? innerWidth / (points.length - 1) : 0;
  const maxValue = Math.max(...points.map((point) => point.value));
  const getX = (index) => MARGIN.left + index * xStep;
  const getY = (value) => MARGIN.top + innerHeight - (value / maxValue) * innerHeight;
  const path = points.map((point, index) => (
    `${index === 0 ? 'M' : 'L'} ${getX(index)} ${getY(point.value)}`
  )).join(' ');

  return <div className='relative overflow-x-auto'>
    <div className='mb-3 flex items-center gap-2 text-xs text-slate-600'>
      <span className='inline-block h-2.5 w-2.5 rounded-full bg-indigo-600' />
      All submitted timesheet months
    </div>
    <div className='relative' style={{width: `${chartWidth}px`}}>
      <svg
        width={chartWidth}
        height={SVG_HEIGHT}
        role='img'
        aria-label='Active students across all submitted timesheet months'
        className='text-xs'
        onMouseLeave={() => setTooltip((current) => ({...current, show: false}))}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((step) => {
          const value = Math.round(maxValue * step);
          const y = MARGIN.top + innerHeight * (1 - step);
          return <g key={step}>
            <line x1={MARGIN.left} x2={chartWidth - MARGIN.right} y1={y} y2={y}
              stroke='rgba(148, 163, 184, 0.22)' />
            <text x={MARGIN.left - 8} y={y} textAnchor='end' dominantBaseline='middle' fill='#94A3B8'>
              {value.toLocaleString()}
            </text>
          </g>;
        })}

        {points.map((point, index) => <g key={`${point.label}-${index}`}>
          <text x={getX(index)} y={SVG_HEIGHT - MARGIN.bottom + 22} textAnchor='middle' fill='#475569'>
            <tspan x={getX(index)}>{MONTH_LABELS[point.date.getMonth()]}</tspan>
            <tspan x={getX(index)} dy='14' fill='#94A3B8'>{point.date.getFullYear()}</tspan>
          </text>
          {(index === 0 || point.date.getMonth() === 0) && <line
            x1={getX(index)} x2={getX(index)} y1={MARGIN.top} y2={MARGIN.top + innerHeight}
            stroke='rgba(99, 102, 241, 0.12)' strokeDasharray='4 5'
          />}
        </g>)}

        <path d={path} fill='none' stroke='#4F46E5' strokeWidth='3'
          strokeLinejoin='round' strokeLinecap='round' />
        {points.map((point, index) => {
          const x = getX(index);
          const y = getY(point.value);
          return <circle
            key={`point-${point.label}-${index}`}
            cx={x} cy={y} r='4' fill='#FFFFFF' stroke='#4F46E5' strokeWidth='2.5'
            tabIndex={0}
            aria-label={`${point.label}: ${point.value.toLocaleString()} active students`}
            onMouseEnter={() => setTooltip({show: true, x, y, label: point.label, value: point.value})}
            onMouseMove={() => setTooltip({show: true, x, y, label: point.label, value: point.value})}
            onFocus={() => setTooltip({show: true, x, y, label: point.label, value: point.value})}
            onBlur={() => setTooltip((current) => ({...current, show: false}))}
          />;
        })}
      </svg>
      {tooltip.show && <div
        className='pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-md'
        style={{left: `${tooltip.x}px`, top: `${Math.max(tooltip.y - 8, 18)}px`}}
      >
        <div className='font-semibold text-slate-900'>{tooltip.label}</div>
        <div className='mt-1 tabular-nums text-slate-600'>{tooltip.value.toLocaleString()} active students</div>
      </div>}
    </div>
  </div>;
};

export default ActiveStudentsAllTimeLineChart;
